import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Play,
  Copy,
  Trash2,
  Star,
  StarOff,
  MoreVertical,
  Search,
  BarChart3,
  Loader2,
  Calendar,
  Clock,
  Download,
  FileText,
  FileSpreadsheet,
  File,
} from 'lucide-react';
import {
  useReportsList,
  useDeleteReport,
  useDuplicateReport,
  useRunReport,
  useUpdateReport,
} from '../../hooks/useAnalytics';
import { CustomReport, analyticsService, type ReportExecutionResult } from '../../services/analytics.service';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { Modal } from '@centraoneplus/ui';

/** Prettify a metric key like "total_tickets" → "Total Tickets". */
function metricLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Parse a server timestamp. The analytics service serializes UTC times WITHOUT
 * a timezone designator (Python datetime.isoformat()), e.g.
 * "2026-10-16T05:25:32.693148"; `new Date()` then reads that as LOCAL time, so a
 * UTC value is shown as-is instead of converted to the user's zone — the report
 * said "Generated 05:25" (UTC) when it was 10:55 IST (BUG-055 / #20578). Treat a
 * timezone-less ISO string as UTC so toLocale* renders in the user's own zone.
 */
export function parseServerDate(s: string): Date {
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(hasTz ? s : `${s}Z`);
}

export default function SavedReports() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [exportMenuOpen, setExportMenuOpen] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // #18940 — the run result, shown in a modal. Run used to discard it (no view).
  const [runResult, setRunResult] = useState<ReportExecutionResult | null>(null);

  const { data: reports, isLoading } = useReportsList(true);
  const deleteReport = useDeleteReport();
  const duplicateReport = useDuplicateReport();
  const runReport = useRunReport();
  const updateReport = useUpdateReport();

  const filteredReports = (reports || []).filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.data_source.toLowerCase().includes(search.toLowerCase()),
  );

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this report?')) return;
    await deleteReport.mutateAsync(id);
    setOpenMenu(null);
  };

  const handleDuplicate = async (report: CustomReport) => {
    await duplicateReport.mutateAsync({ id: report.id, name: `${report.name} (Copy)` });
    setOpenMenu(null);
  };

  const handleRun = async (id: string) => {
    setOpenMenu(null);
    // #18940 — show the result instead of discarding it.
    const result = await runReport.mutateAsync({ id });
    setRunResult(result);
  };

  const handleToggleFavorite = async (report: CustomReport) => {
    await updateReport.mutateAsync({
      id: report.id,
      data: { is_favorite: !report.is_favorite },
    });
  };

  const handleExport = async (reportId: string, format: 'pdf' | 'csv' | 'excel') => {
    setExporting(true);
    setExportMenuOpen(null);
    try {
      const blob = await analyticsService.exportReport(reportId, format);
      const ext = format === 'excel' ? 'xlsx' : format;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${reportId.slice(0, 8)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr?: string) =>
    dateStr
      ? parseServerDate(dateStr).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '';

  return (
    <div className="py-4 pr-4 space-y-6">
      {/* Header */}
<div className="border border-[var(--co-line)] bg-[var(--co-raised)] px-5 py-[18px]" style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}>
      <div className="flex items-center justify-between gap-[10px]">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-[var(--co-ink)]">Saved Reports</h1>
          <p className="mt-1 text-sm text-[var(--co-ink-3)]">
            Manage your custom analytics reports
          </p>
        </div>
        <div className="flex h-[34px] flex-none items-center">
          <PageHeaderMeta />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
          <div className="flex-1" />
          <button
            onClick={() => navigate('/reports/builder')}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-[10px] hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Report
          </button>
      </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search reports..."
          className="input-field w-full pl-10"
        />
      </div>

      {/* Reports List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      ) : filteredReports.length === 0 ? (
        <div className="text-center py-16">
          <BarChart3 className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">
            {search ? 'No matching reports' : 'No reports yet'}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {search
              ? 'Try a different search term'
              : 'Create your first custom report to get started'}
          </p>
          {!search && (
            <button
              onClick={() => navigate('/reports/builder')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Create Report
            </button>
          )}
        </div>
      ) : (
        <div className="card overflow-visible bg-white dark:bg-gray-800">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Report
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden sm:table-cell">
                  Data Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden md:table-cell">
                  Chart Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden lg:table-cell">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase hidden lg:table-cell">
                  Schedule
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredReports.map((report, index) => (
                <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleFavorite(report)}
                        className="text-gray-400 hover:text-yellow-500 transition-colors dark:text-gray-500"
                      >
                        {report.is_favorite ? (
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        ) : (
                          <StarOff className="h-4 w-4" />
                        )}
                      </button>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{report.name}</p>
                        {report.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                            {report.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 capitalize">
                      {report.data_source}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-sm text-gray-600 dark:text-gray-300 capitalize">
                      {report.chart_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatDate(report.created_at)}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {report.schedules && report.schedules.length > 0 ? (
                      <div className="flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                        <Clock className="h-3.5 w-3.5" />
                        {report.schedules[0].frequency}
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400 dark:text-gray-500">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Export dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setExportMenuOpen(exportMenuOpen === report.id ? null : report.id)}
                          disabled={exporting}
                          className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-green-600 transition-colors dark:text-gray-400"
                          title="Export"
                        >
                          {exporting ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </button>
                        {exportMenuOpen === report.id && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={() => setExportMenuOpen(null)}
                            />
                            <div
                              className={`absolute right-0 w-36 bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-black/5 dark:ring-white/10 border border-gray-200 dark:border-gray-700 z-50 ${
                                index === filteredReports.length - 1 && filteredReports.length > 1
                                  ? 'bottom-full mb-1'
                                  : 'mt-1'
                              }`}
                            >
                            <button
                              onClick={() => handleExport(report.id, 'pdf')}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-t-lg"
                            >
                              <FileText className="h-4 w-4 text-red-500" />
                              PDF
                            </button>
                            <button
                              onClick={() => handleExport(report.id, 'csv')}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <File className="h-4 w-4 text-blue-500" />
                              CSV
                            </button>
                            <button
                              onClick={() => handleExport(report.id, 'excel')}
                              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-b-lg"
                            >
                              <FileSpreadsheet className="h-4 w-4 text-green-500" />
                              Excel
                            </button>
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => handleRun(report.id)}
                        disabled={runReport.isPending}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-primary-600 transition-colors dark:text-gray-400"
                        title="Run report"
                      >
                        {runReport.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDuplicate(report)}
                        disabled={duplicateReport.isPending}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-blue-600 transition-colors dark:text-gray-400"
                        title="Duplicate"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(report.id)}
                        disabled={deleteReport.isPending}
                        className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-red-600 transition-colors dark:text-gray-400"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* #18940 — report run results. Run used to send a request and discard
          the response; this shows the computed metrics for the window. */}
      <Modal
        isOpen={!!runResult}
        onClose={() => setRunResult(null)}
        title={runResult?.report_name || 'Report results'}
        size="lg"
      >
        {runResult && (
          <div className="space-y-4">
            {runResult.time_range?.start && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(runResult.time_range.start)} → {formatDate(runResult.time_range.end)}
              </p>
            )}
            {(runResult.results?.length ?? 0) === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No metrics returned for this report.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                    <th className="py-2 pr-4 font-medium">Metric</th>
                    <th className="py-2 font-medium text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {runResult.results!.map((r) => (
                    <tr key={r.metric} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-4 text-gray-900 dark:text-white">{metricLabel(r.metric)}</td>
                      <td className="py-2 text-right font-mono tabular-nums text-gray-900 dark:text-white">
                        {typeof r.value === 'number' ? r.value.toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {runResult.generated_at && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Generated {parseServerDate(runResult.generated_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
